import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { Observable } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import { AdminPaymentMethodCollectionItem } from '../../models/admin-payment-method-collection-item';
import { AdminPaymentMethodCollectionFilter } from '../../models/admin-payment-method-collection-filter';
import { AdminPaymentMethodListItemAction } from '../../enums/admin-payment-method-list-item-action';
import { AdminPaymentMethodListCollectionAction } from '../../enums/admin-payment-method-list-collection-action';
import { AdminPaymentMethodDataSourceService } from '../../services/admin-payment-method-data-source.service';
import { AdminPaymentMethodListCustomCellTemplateComponent } from '../../components/admin-payment-method-list-custom-cell-template/admin-payment-method-list-custom-cell-template.component';

@Component({
  selector: 'app-admin-payment-method-list',
  imports: [TableComponent, AdminPaymentMethodListCustomCellTemplateComponent],
  templateUrl: './admin-payment-method-list.component.html',
  styleUrl: './admin-payment-method-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPaymentMethodListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminPaymentMethodCollectionItem, AdminPaymentMethodCollectionFilter, AdminPaymentMethodListItemAction, AdminPaymentMethodListCollectionAction>).create();
  /* prettier-ignore */
  private readonly paymentMethodDataSource = inject(AdminPaymentMethodDataSourceService);

  protected config!: TableConfig<
    AdminPaymentMethodCollectionItem,
    AdminPaymentMethodCollectionFilter,
    AdminPaymentMethodListItemAction,
    AdminPaymentMethodListCollectionAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminPaymentMethodListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.PaymentMethodDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminPaymentMethodListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.PaymentMethodEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('description')
          .build(),

        builder //
          .initText('name')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initCustom('previewFile')
          .width('8rem')
          .label('Grafika')
          .sortable(false)
          .filterable(false)
          .build(),

        builder //
          .initText('name')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),

        builder //
          .initBoolean('isActive')
          .label('Dostępna')
          .sortable(false)
          .filterable(false)
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminPaymentMethodCollectionFilter>,
        ): Observable<TableDataPayload<AdminPaymentMethodCollectionItem>> =>
          this.paymentMethodDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }
}
